import React, { useRef } from "react";
import { Helmet } from "react-helmet-async";
import Footer from "../../components/Footer";
import { useArticlesContext } from "../../context/ArticlesContext";
import MobileMapOverlay from "../../components/MobileMapOverlay";
import SocialMedia from "../../components/SocialMedia";
import Marker from "../../components/Marker";
import Top from "../../components-top/Top";
import Left from "../../components-top/Left";
import Right from "../../components-top/Right";
import Bottom from "../../components-top/Bottom";

export default function ArticleHiddenRainforestTrails() {
  // 1) Provide a unique reference ID (the slug in your ArticlesContext)
  const ref_id = "hidden-vancouver-rainforest-trails";

  // 2) Access articles from your context (optional usage)
  const { articles } = useArticlesContext();
  const ref_data = articles.find((art) => art.slug === ref_id);

  // =========================================================
  // ========== SECTION TITLES IDS (Labels Map) ==============
  // =========================================================
  const labelsMap = {
    intro: "Introduction",
    "section-lighthouse": "Lighthouse Park",
    "section-lynncanyon": "Lynn Canyon Park",
    "section-pacificspirit": "Pacific Spirit Park",
    "section-norvan": "Norvan Falls",
    "section-whyte": "Whyte Lake",
    "section-cypressfalls": "Cypress Falls Park",
    "section-jugisland": "Jug Island Trail",
    "section-mystery": "Mystery Lake",
    "local-regulations": "Local Regulations & Best Practices",
    faq: "FAQ",
    conclusion: "Conclusion",
    references: "References",
  };

  // ===================================
  // 4) References Data
  // ===================================
  const referencesData = [
    {
      url: "https://vancouver.ca/parks-recreation-culture/parks.aspx",
      label: "Official Vancouver Parks Info",
    },
    {
      url: "https://bcparks.ca/mount-seymour-park/",
      label: "BC Parks – Mount Seymour",
    },
    {
      url: "https://www.metrovancouver.org/services/parks/Pages/default.aspx",
      label: "Metro Vancouver Regional Parks",
    },
  ];

  // ===================================
  // 5) Locations for the Leaflet map
  // ===================================
  // We have 8 locations corresponding to the 8 trails:
  const locations = [
    {
      id: "section-lighthouse",
      lat: 49.33611,
      lng: -123.26194,
      popupText: "Lighthouse Park (West Vancouver)",
    },
    {
      id: "section-lynncanyon",
      lat: 49.33380,
      lng: -123.01750,
      popupText: "Lynn Canyon Park (North Vancouver)",
    },
    {
      id: "section-pacificspirit",
      lat: 49.25,
      lng: -123.21,
      popupText: "Pacific Spirit Regional Park (Vancouver)",
    },
    {
      id: "section-norvan",
      lat: 49.35962,
      lng: -123.02795,
      popupText: "Norvan Falls (Lynn Headwaters Park)",
    },
    {
      id: "section-whyte",
      lat: 49.36075,
      lng: -123.25877,
      popupText: "Whyte Lake Trail (West Vancouver)",
    },
    {
      id: "section-cypressfalls",
      lat: 49.35205,
      lng: -123.24083,
      popupText: "Cypress Falls Park (West Vancouver)",
    },
    {
      id: "section-jugisland",
      lat: 49.31226,
      lng: -122.92490,
      popupText: "Jug Island Trail (Belcarra Regional Park)",
    },
    {
      id: "section-mystery",
      lat: 49.36660,
      lng: -122.94825,
      popupText: "Mystery Lake (Mount Seymour Park)",
    },
  ];

  // Keep references to textual sections for scroll linking
  const spotRefs = useRef({});
  const setSpotRef = (id, el) => {
    spotRefs.current[id] = el;
  };

  // Desktop map reference
  const mapRef = useRef(null);

  // MOBILE MAP
  const mobileMapRef = useRef(null);

  return (
    <>
      {/* ============ SEO & METADATA ============ */}
      <Helmet>
        <title>Discover Vancouver’s Hidden Rainforest Trails: The Ultimate Guide</title>
        <meta
          name="description"
          content="Explore the lesser-known rainforest trails around Vancouver. Discover tranquil forests, waterfalls, and lakes—complete with detailed tips, coordinates, and FAQs."
        />
        <meta
          name="keywords"
          content="Vancouver, hiking, rainforest trails, hidden gems, waterfalls, Pacific Spirit Park, Lynn Canyon, Lighthouse Park"
        />
        <meta name="robots" content="index, follow" />
        <link
          rel="canonical"
          href="https://www.bcouver.com/articles/hidden-vancouver-rainforest-trails.html"
        />

        {/* Open Graph / Facebook */}
        <meta
          property="og:title"
          content="Discover Vancouver’s Hidden Rainforest Trails: The Ultimate Guide"
        />
        <meta
          property="og:description"
          content="Explore Vancouver's lush, lesser-known rainforest paths away from the crowds. Detailed locations, tips, and best practices included."
        />
        <meta
          property="og:image"
          content="https://bcouver.com/assets/images/hidden-vancouver-rainforest-trails.jpg"
        />
        <meta
          property="og:url"
          content="https://www.bcouver.com/articles/hidden-vancouver-rainforest-trails.html"
        />
        <meta property="og:type" content="article" />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content="Discover Vancouver’s Hidden Rainforest Trails: The Ultimate Guide"
        />
        <meta
          name="twitter:description"
          content="A thorough guide to Vancouver's hidden rainforest trails, complete with coordinates, tips, and more."
        />
        <meta
          name="twitter:image"
          content="https://bcouver.com/assets/images/hidden-vancouver-rainforest-trails.jpg"
        />
      </Helmet>

      {/* ============ TOP NAVBAR + HERO ============ */}
      <Top ref_id={ref_id} />

      <main>
        <div className="container article-main">
          {/* LEFT COLUMN (TableOfContents + Map) */}
          <Left
            labelsMap={labelsMap}
            locations={locations}
            mapRef={mapRef}
            spotRefs={spotRefs}
          />

          {/* ============ ARTICLE CONTENT ============ */}
          <article className="article-content">
            {/* Featured Image */}
            <img
              src={ref_data?.image || "https://bcouver.com/assets/images/article-fallback.jpg"}
              alt="Misty rainforest with tall trees"
              className="featured-image"
            />

            {/* Social Share */}
            <SocialMedia/>

            {/* SECTION: Introduction */}
            <h2 id="intro">Introduction</h2>
            <p>
              Vancouver’s reputation for natural beauty is well-earned. Beyond
              popular parks and viewpoints, a network of{" "}
              <strong>hidden rainforest trails</strong> awaits, weaving through
              lush greenery, moss-draped trees, and secluded waterfalls. This
              guide takes you off the beaten path to some of the city's best-kept
              secrets—ideal for hikers looking for a tranquil forest experience.
              We’ve included coordinates, practical tips, and important local
              regulations to help you enjoy these trails safely and responsibly.
            </p>

            {/* SECTION: Lighthouse Park */}
            <h2 id="section-lighthouse">Lighthouse Park</h2>
            <p ref={(el) => setSpotRef("section-lighthouse", el)} style={{ scrollMarginTop: "80px" }}>
              <strong>Lighthouse Park</strong>{" "}
              <Marker
                coordY={49.33611}
                coordX={-123.26194}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
              —Located in West Vancouver, this old-growth sanctuary features
              towering cedars and Douglas firs over 500 years old. Multiple
              trails lead you through a serene forest down to rocky shoreline
              viewpoints with sweeping ocean vistas. This park is popular
              among locals for easy, scenic walks, yet remains quieter than
              more touristy spots. It’s accessible year-round, with easy
              terrain suitable for all ages.
            </p>

            {/* SECTION: Lynn Canyon Park */}
            <h2 id="section-lynncanyon">Lynn Canyon Park</h2>
            <p ref={(el) => setSpotRef("section-lynncanyon", el)} style={{ scrollMarginTop: "80px" }}>
              <strong>Lynn Canyon Park</strong>{" "}
              <Marker
                coordY={49.33380}
                coordX={-123.01750}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
              —North Vancouver’s free suspension bridge and dense rainforest
              are the main draws here. The bridge hangs 50 meters above a mossy
              canyon, leading to a network of trails, waterfalls, and the
              popular 30-Foot Pool swimming hole. Accessible year-round, it’s
              especially breathtaking in spring when waterfalls are at their
              peak flow, but do exercise caution on the slick stairs and near
              the canyon edges.
            </p>

            {/* SECTION: Pacific Spirit Regional Park */}
            <h2 id="section-pacificspirit">Pacific Spirit Regional Park</h2>
            <p ref={(el) => setSpotRef("section-pacificspirit", el)} style={{ scrollMarginTop: "80px" }}>
              <strong>Pacific Spirit Regional Park</strong>{" "}
              <Marker
                coordY={49.25}
                coordX={-123.21}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
              —Just minutes from downtown Vancouver, this sprawling urban
              rainforest offers over 50 km of interconnected trails. From
              fern-lined paths to sandy shorelines, it’s a haven for walkers,
              joggers, and nature enthusiasts. Be sure to visit Camosun Bog for
              a unique ecosystem experience. Most trails are relatively flat,
              making it an ideal choice for beginners and families seeking a
              peaceful forest escape.
            </p>

            {/* SECTION: Norvan Falls */}
            <h2 id="section-norvan">Norvan Falls</h2>
            <p ref={(el) => setSpotRef("section-norvan", el)} style={{ scrollMarginTop: "80px" }}>
              <strong>Norvan Falls</strong>{" "}
              <Marker
                coordY={49.35962}
                coordX={-123.02795}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
              —Tucked away in Lynn Headwaters Regional Park, this 14 km
              round-trip hike rewards you with a stunning 30-meter waterfall.
              The forested trail winds along Lynn Creek, gradually leading you
              into a quiet cedar and fir forest. While it’s mostly flat, it
              requires moderate endurance. It’s accessible year-round, though
              expect muddy sections after rain or in winter.
            </p>

            {/* SECTION: Whyte Lake */}
            <h2 id="section-whyte">Whyte Lake</h2>
            <p ref={(el) => setSpotRef("section-whyte", el)} style={{ scrollMarginTop: "80px" }}>
              <strong>Whyte Lake Trail</strong>{" "}
              <Marker
                coordY={49.36075}
                coordX={-123.25877}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
              —A short, peaceful forest hike in West Vancouver, this
              5.2 km round-trip trail leads to a small alpine lake surrounded
              by mossy cedars. A wooden dock offers a tranquil place to rest or
              dip your feet in summer. Gentle slopes and plenty of shade make
              it perfect for beginners, though do watch for a few rooty
              sections. The lake is accessible year-round, but it’s best in
              warmer months if you plan to swim.
            </p>

            {/* SECTION: Cypress Falls Park */}
            <h2 id="section-cypressfalls">Cypress Falls Park</h2>
            <p ref={(el) => setSpotRef("section-cypressfalls", el)} style={{ scrollMarginTop: "80px" }}>
              <strong>Cypress Falls Park</strong>{" "}
              <Marker
                coordY={49.35205}
                coordX={-123.24083}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
              —Another West Vancouver hidden gem, Cypress Falls features two
              impressive waterfalls in a short 2-3 km loop. You’ll hike through
              a canyon shaded by towering old-growth cedars. This trail is easy
              enough for families and offers a magical rainforest ambiance,
              especially on rainy days when water levels surge.
            </p>

            {/* SECTION: Jug Island Trail */}
            <h2 id="section-jugisland">Jug Island Trail</h2>
            <p ref={(el) => setSpotRef("section-jugisland", el)} style={{ scrollMarginTop: "80px" }}>
              <strong>Jug Island Trail</strong>{" "}
              <Marker
                coordY={49.31226}
                coordX={-122.92490}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
              —Nestled in Belcarra Regional Park, this 5.5 km out-and-back hike
              leads to a secluded beach overlooking Indian Arm fjord. The route
              ascends through second-growth forest and descends onto a quiet
              pebble beach with views of Jug Island. Popular for picnics and
              spotting wildlife like bald eagles or seals, it’s best visited in
              spring through fall.
            </p>

            {/* SECTION: Mystery Lake */}
            <h2 id="section-mystery">Mystery Lake</h2>
            <p ref={(el) => setSpotRef("section-mystery", el)} style={{ scrollMarginTop: "80px" }}>
              <strong>Mystery Lake</strong>{" "}
              <Marker
                coordY={49.36660}
                coordX={-122.94825}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
              —A short but steep summer hike in Mount Seymour Provincial Park,
              this 3 km round-trip trail offers a picturesque alpine lake at
              around 1,000 m elevation. The climb is worth it for a refreshing
              swim and panoramic mountain views. Accessible only from July to
              October due to snow, Mystery Lake remains a lesser-known oasis
              compared to other North Shore summer spots.
            </p>

            {/* SECTION: Local Regulations & Best Practices */}
            <h2 id="local-regulations">Local Regulations & Best Practices</h2>
            <p>
              When exploring Vancouver’s wilderness, always stay on marked
              trails to protect fragile ecosystems. Pack out all garbage and
              leave no trace of your visit. Keep dogs on leash unless otherwise
              posted, and remember that wildlife like bears and cougars may be
              present—carry bear spray if heading into remote areas. Follow
              park rules regarding campfires (generally prohibited) and swimming
              in canyon rivers (like Lynn Canyon), which can be dangerous. Check
              official websites for any trail advisories or closures before
              heading out.
            </p>

            {/* SECTION: FAQ */}
            <h2 id="faq">FAQ</h2>
            <div className="faq-section">
              <h3>1. Are these trails good for beginners?</h3>
              <p>
                Yes. Many are suitable for novices or families, particularly
                Lighthouse Park, Pacific Spirit, and Whyte Lake. Trails like
                Norvan Falls or Mystery Lake require more endurance or have
                steeper sections, but they are still manageable for those with
                moderate fitness.
              </p>

              <h3>2. Do I need any permits or fees?</h3>
              <p>
                Most trails mentioned are free and do not require permits.
                However, some parking areas (e.g., Belcarra) may have a small
                fee. Always check signs at the trailhead to confirm.
              </p>

              <h3>3. What’s the best time of year to visit?</h3>
              <p>
                Most low-elevation rainforest trails are accessible year-round,
                though they can be muddy in winter. Higher elevation trails like
                Mystery Lake are best from July to October. Spring is ideal for
                waterfalls; summer is great for swimming holes; fall offers
                colorful foliage and fewer crowds.
              </p>
            </div>

            {/* SECTION: Conclusion */}
            <h2 id="conclusion">Conclusion</h2>
            <p>
              Vancouver’s hidden rainforest trails showcase some of the most
              pristine old-growth forests, waterfalls, and lakes in British
              Columbia—all without the crowding of more famous spots. Whether
              you’re after a gentle family walk or a moderate day hike, these
              trails deliver serene natural beauty close to the city. By
              respecting local guidelines and treading lightly, you’ll help
              preserve these ecosystems for future generations. Grab your
              hiking boots, pack your essentials, and immerse yourself in
              Vancouver’s lush rainforest wonders.
            </p>

            {/* BOTTOM COMPONENT: references, author, related articles, etc. */}
            <Bottom referencesData={referencesData} ref_data={ref_data} />
          </article>

          {/* RIGHT SIDE COLUMN */}
          <Right currentArticle={ref_data} />
        </div>
      </main>

      {/* MOBILE MAP OVERLAY */}
      <MobileMapOverlay
        ref={mobileMapRef}
        markers={locations}
        spotRefs={spotRefs}
      />

      <Footer />
    </>
  );
}